import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';


import { Box, Typography, IconButton, MenuItem, Select, FormControl, InputLabel, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress, FormControlLabel, Switch, Grid, useTheme, useMediaQuery, Snackbar, Checkbox, ListItemText, ListSubheader } from '@mui/material';
import { Add as AddIcon, Receipt as ReceiptIcon, Email as EmailIcon } from '@mui/icons-material';

import ServiceService from '../services/serviceService';
import ServiceTemplateService from '../services/serviceTemplateService';
import ProjectService from '../../projects/services/projectService';
import settingsService from '../../settings/services/settingsService';
import PageHeader from '../../../components/common/PageHeader';
import SearchHeader from '../../../components/common/SearchHeader';
import PaginationComponent from '../../../components/common/PaginationComponent';
import FormBuilder from '../../../components/common/FormBuilder';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import MetricsCard from '../../../components/common/MetricsCard';
import ClientSelect from '../../../components/common/ClientSelect';
import ServiceTable from '../components/ServiceTable';
import SchedulerPanel from '../components/SchedulerPanel';
import InvoiceFormModal from '../../invoices/components/InvoiceFormModal';
import { createServiceSchema, updateServiceSchema } from '../schemas/serviceFormSchema';
import { computeListingStatistics, filterServicesForListing } from '../utils/serviceListingFilters';

const CHARGE_PERIOD_VALUES = ['monthly', 'quarterly', 'semi_annual', 'annual', 'biennial'];

const resolveChargeFilter = (saved) => {
  if (saved.charge && saved.charge !== 'all') return saved.charge;
  if (saved.subscription && saved.subscription !== 'all') return saved.subscription;
  if (saved.billingCategory === 'one_time') return 'one_time';
  if (saved.billingCategory === 'subscription') return 'subscription';
  return 'all';
};

const resolveValidityFilter = (saved) => {
  if (saved.validity && saved.validity !== 'all') return saved.validity;
  if (saved.inForce && saved.inForce !== 'all') return saved.inForce;
  if (saved.expiration && saved.expiration !== 'all') {
    return saved.expiration === 'expired' ? 'expired' : saved.expiration;
  }
  if (saved.status === 'expired') return 'expired';
  return 'all';
};

const resolveStatusFilter = (saved) => {
  const status = saved.status || 'all';
  if (status === 'active' || status === 'expired') return 'all';
  return status;
};

const ServiceListingPage = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Helper function for currency formatting
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0,00 €';
    }
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  // Data states
  const isInitialMount = useRef(true);
  const skipFilterPageReset = useRef(true);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('services');
  
  // Modal states
  const [serviceSelectionModal, setServiceSelectionModal] = useState(false);
  const [success, setSuccess] = useState(null);
  
  
  // Multi-selection states
  const [selectedServices, setSelectedServices] = useState([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [preSelectedData, setPreSelectedData] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: true,
    email_warning_days: [10, 7, 3],
  });
  const [sendingExpirationEmailId, setSendingExpirationEmailId] = useState(null);
  const [sendingBulkExpirationEmail, setSendingBulkExpirationEmail] = useState(false);
  const expirationEmailLockRef = useRef(false);

  // Statistics state
  const [statistics, setStatistics] = useState({
    total_services: 0,
    total_credit: 0,
    total_cost_with_vat: 0,
    net_cost: 0,
    total_paid: 0,
    balance: 0
  });
  
 // Filter states (restore from localStorage if available)
const savedFilters = useMemo(() => {
  try {
    const stored = localStorage.getItem('serviceListing.filters');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed;
    }
    return {};
  } catch (e) {
    console.error('❌ [ERROR] Loading filters:', e);
    return {};
  }
}, []); // IMPORTANT: Empty array means this runs only once on mount

const [searchTerm, setSearchTerm] = useState(() => savedFilters.search || '');

// Use lazy initialization for filters that should persist
const [clientStatusFilter, setClientStatusFilter] = useState(() => {
  const value = savedFilters.clientStatus || 'active';
  return value;
});

const [clientFilter, setClientFilter] = useState(() => {
  const value = savedFilters.client || 'all';
  return value;
});

const [paymentStatusFilter, setPaymentStatusFilter] = useState(() => {
  const value = savedFilters.paymentStatus || 'all';
  return value;
});

const [serviceTypeFilter, setServiceTypeFilter] = useState(() => {
  const value = savedFilters.serviceType || 'all';
  return value;
});

const [chargeFilter, setChargeFilter] = useState(() => resolveChargeFilter(savedFilters));

const [statusFilter, setStatusFilter] = useState(() => resolveStatusFilter(savedFilters));

const [yearFilter, setYearFilter] = useState(() => {
  const value = savedFilters.years;
  if (Array.isArray(value)) {
    return value.map(Number).filter((year) => !Number.isNaN(year));
  }
  return [];
});

const [validityFilter, setValidityFilter] = useState(() => resolveValidityFilter(savedFilters));

const yearOptions = useMemo(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear + 1; year >= 2018; year -= 1) {
    years.push(year);
  }
  return years;
}, []);
  
  // Pagination states (restore from localStorage if available)
  const savedPaging = (() => {
    try {
      return JSON.parse(localStorage.getItem('serviceListing.paging')) || {};
    } catch { return {}; }
  })();
  const [currentPage, setCurrentPage] = useState(savedPaging.page || 1);
  const [itemsPerPage, setItemsPerPage] = useState(savedPaging.pageSize || 10);
  const [totalServices, setTotalServices] = useState(0);

  // Sorting states (restore from localStorage if available)
  const savedSort = (() => {
    try {
      return JSON.parse(localStorage.getItem('serviceListing.sort')) || {};
    } catch { return {}; }
  })();
  const [sortField, setSortField] = useState(savedSort.field || 'name');
  const [sortDirection, setSortDirection] = useState(savedSort.direction || 'asc');

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  
  // Form state for client-project dependency
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  
  // Template selection state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState(null);
  
  // Form reset state
  const [formResetKey, setFormResetKey] = useState(0);

  const getCommonFilterParams = useCallback(() => {
    const params = {};

    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }
    if (clientStatusFilter !== 'all') {
      params.client_status = clientStatusFilter;
    }
    if (clientFilter !== 'all') {
      params.client_id = clientFilter;
    }
    if (paymentStatusFilter !== 'all') {
      params.payment_status = paymentStatusFilter;
    }
    if (serviceTypeFilter !== 'all') {
      params.service_type = serviceTypeFilter;
    }
    if (chargeFilter === 'one_time' || chargeFilter === 'subscription') {
      params.billing_category = chargeFilter;
    } else if (CHARGE_PERIOD_VALUES.includes(chargeFilter)) {
      params.billing_category = 'subscription';
      params.billing_period = chargeFilter;
    }
    if (validityFilter === 'in_force' || validityFilter === 'expired') {
      params.in_force = validityFilter;
    } else if (validityFilter !== 'all') {
      params.expiration_filter = validityFilter;
    }
    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }
    if (yearFilter.length > 0) {
      params.years = yearFilter;
    }

    return params;
  }, [
    searchTerm,
    clientStatusFilter,
    clientFilter,
    paymentStatusFilter,
    serviceTypeFilter,
    chargeFilter,
    validityFilter,
    statusFilter,
    yearFilter,
  ]);

  const hasClientListingFilters = chargeFilter !== 'all' || yearFilter.length > 0 || validityFilter !== 'all';

  const applyClientListingFilters = useCallback((list) => {
    return filterServicesForListing(list, {
      chargeFilter,
      years: yearFilter,
      validity: validityFilter,
    });
  }, [chargeFilter, yearFilter, validityFilter]);

  // Load services function
  const loadServices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        ...getCommonFilterParams(),
        skip: hasClientListingFilters ? 0 : (currentPage - 1) * itemsPerPage,
        limit: hasClientListingFilters ? 500 : itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection,
      };
      
      const result = await ServiceService.getAll(params);
      
      if (result.success) {
        const raw = result.data || [];
        const filtered = applyClientListingFilters(raw);
        if (hasClientListingFilters) {
          const start = (currentPage - 1) * itemsPerPage;
          setServices(filtered.slice(start, start + itemsPerPage));
          setTotalServices(filtered.length);
          setStatistics(computeListingStatistics(filtered));
        } else {
          setServices(filtered);
          setTotalServices(result.total || filtered.length);
        }
      } else {
        setError(result.error);
        setServices([]);
        setTotalServices(0);
      }
    } catch (err) {
      console.error('Error loading services:', err);
      setError('Σφάλμα φόρτωσης υπηρεσιών');
      setServices([]);
      setTotalServices(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sortField, sortDirection, getCommonFilterParams, hasClientListingFilters, applyClientListingFilters]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    if (hasClientListingFilters) {
      return;
    }
    try {
      const result = await ServiceService.getStatistics(getCommonFilterParams());
      if (result.success) {
        setStatistics(result.data || {
          total_services: 0,
          total_credit: 0,
          total_cost_with_vat: 0,
          net_cost: 0,
          total_paid: 0,
          balance: 0
        });
      } else {
        console.error('Error loading statistics:', result.error);
        setStatistics({
          total_services: 0,
          total_credit: 0,
          total_cost_with_vat: 0,
          net_cost: 0,
          total_paid: 0,
          balance: 0
        });
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
      setStatistics({
        total_services: 0,
        total_credit: 0,
        total_cost_with_vat: 0,
        net_cost: 0,
        total_paid: 0,
        balance: 0
      });
    }
  }, [getCommonFilterParams, hasClientListingFilters]);

  // Load projects and templates for dropdowns (clients use ClientSelect with backend search)
  const projectsTemplatesLoadedRef = useRef(false);
  const loadProjectsAndTemplates = useCallback(async () => {
    // Prevent duplicate calls (React StrictMode)
    if (projectsTemplatesLoadedRef.current) return;
    projectsTemplatesLoadedRef.current = true;
    
    try {
      // Load projects (max limit is 500 according to backend)
      const projectsResult = await ProjectService.getAll({ limit: 500 });
      if (projectsResult.success) {
        setProjects(projectsResult.data || []);
      } else {
        console.error('Failed to load projects:', projectsResult.error);
      }

      // Load templates
      const templatesResult = await ServiceTemplateService.getAll({ active_only: true, limit: 100 });
      if (templatesResult.success) {
        setTemplates(templatesResult.data || []);
      } else {
        console.error('Failed to load templates:', templatesResult.error);
      }
    } catch (err) {
      console.error('Error loading projects/templates:', err);
      setError('Σφάλμα φόρτωσης δεδομένων');
    }
  }, []);

  // Load projects and templates only once on mount
  useEffect(() => {
    loadProjectsAndTemplates();
  }, [loadProjectsAndTemplates]);

  useEffect(() => {
    const loadNotificationSettings = async () => {
      const result = await settingsService.getNotificationSettings();
      if (result.success && result.data) {
        setNotificationSettings(result.data);
      }
    };
    loadNotificationSettings();
  }, []);

  // Reload templates when create modal opens (ensures fresh data, fixes race if modal opens before initial load)
  const loadTemplatesForModal = useCallback(async () => {
    try {
      const templatesResult = await ServiceTemplateService.getAll({ active_only: true, limit: 100 });
      if (templatesResult.success) {
        const data = templatesResult.data;
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error loading templates for modal:', err);
    }
  }, []);

  useEffect(() => {
    if (createModalOpen) {
      loadTemplatesForModal();
    }
  }, [createModalOpen, loadTemplatesForModal]);

  // Persist filters to localStorage
  useEffect(() => {
    localStorage.setItem('serviceListing.filters', JSON.stringify({
      search: searchTerm,
      clientStatus: clientStatusFilter,
      client: clientFilter,
      paymentStatus: paymentStatusFilter,
      serviceType: serviceTypeFilter,
      charge: chargeFilter,
      validity: validityFilter,
      status: statusFilter,
      years: yearFilter,
    }));
  }, [searchTerm, clientStatusFilter, clientFilter, paymentStatusFilter, serviceTypeFilter, chargeFilter, validityFilter, statusFilter, yearFilter]);

  // Persist pagination to localStorage
  useEffect(() => {
    localStorage.setItem('serviceListing.paging', JSON.stringify({
      page: currentPage,
      pageSize: itemsPerPage
    }));
  }, [currentPage, itemsPerPage]);

  // Persist sorting to localStorage
  useEffect(() => {
    localStorage.setItem('serviceListing.sort', JSON.stringify({
      field: sortField,
      direction: sortDirection
    }));
  }, [sortField, sortDirection]);

  // Load services when filters change
  useEffect(() => {
    loadServices();
  }, [currentPage, itemsPerPage, searchTerm, clientStatusFilter, clientFilter, paymentStatusFilter, serviceTypeFilter, chargeFilter, validityFilter, statusFilter, yearFilter, sortField, sortDirection]);

  // Load statistics when any relevant filter changes
  useEffect(() => {
    loadStatistics();
  }, [searchTerm, clientStatusFilter, clientFilter, paymentStatusFilter, serviceTypeFilter, chargeFilter, validityFilter, statusFilter, yearFilter, loadStatistics]);

  useEffect(() => {
    if (skipFilterPageReset.current) {
      skipFilterPageReset.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchTerm, clientStatusFilter, clientFilter, paymentStatusFilter, serviceTypeFilter, chargeFilter, validityFilter, statusFilter, yearFilter]);

  // Reset client filter when client status filter changes
  useEffect(() => {
    // Don't reset on initial mount (when loading from localStorage)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Only reset when user manually changes clientStatusFilter
    setClientFilter('all');
  }, [clientStatusFilter]);
  

  // Get filtered projects based on selected client
  const getFilteredProjects = useCallback(() => {
    if (!selectedClientId) {
      return [];
    }
    return projects.filter(project => project.client_id === selectedClientId);
  }, [projects, selectedClientId]);

  // Get dynamic service type options (only from database templates)
  const getServiceTypeOptions = useCallback(() => {
    const templateList = Array.isArray(templates) ? templates : [];
    // Αν δεν υπάρχουν πρότυπα, μην εμφανίζουμε γενικούς τύπους – μόνο τα δικά σου πρότυπα
    if (templateList.length === 0) {
      return [];
    }

    // Group templates by service type
    const templatesByType = templateList.reduce((acc, template) => {
      if (!acc[template.service_type]) {
        acc[template.service_type] = [];
      }
      acc[template.service_type].push(template);
      return acc;
    }, {});

    // Create options only from existing service types in templates (user's packages first)
    const serviceTypes = [...new Set(templateList.map(t => t.service_type))];
    
    const serviceTypeLabels = {
      'development': 'Ανάπτυξη',
      'hosting': 'Hosting',
      'maintenance': 'Συντήρηση',
      'seo': 'SEO',
      'design': 'Σχεδιασμός',
      'domain': 'Domain',
      'application': 'Εφαρμογή (SaaS)',
      'support': 'Υποστήριξη',
      'consulting': 'Σύμβουλος',
      'training': 'Εκπαίδευση',
      'license': 'Άδεια Λογισμικού',
      'other': 'Άλλο'
    };

    // Μόνο τα δικά σου πρότυπα – όχι γενικοί τύποι (Εφαρμογή, Άλλο κλπ)
    return serviceTypes.map(serviceType => {
      const typeTemplates = templatesByType[serviceType];
      const templateOptions = typeTemplates.map(template => ({
        value: `template_${template.id}`,
        label: `${template.name} (€${template.default_price})`
      }));
      return {
        value: serviceType,
        label: `${serviceTypeLabels[serviceType] || serviceType} (${typeTemplates.length} πρότυπα)`,
        options: templateOptions
      };
    });
  }, [templates]);

  // Απλές επιλογές τύπου για επεξεργασία (χωρίς πρότυπα — το select κρατά service_type, όχι template_*)
  const getEditServiceTypeOptions = useCallback((currentType) => {
    const templateList = Array.isArray(templates) ? templates : [];
    const serviceTypeLabels = {
      development: 'Ανάπτυξη',
      hosting: 'Hosting',
      maintenance: 'Συντήρηση',
      seo: 'SEO',
      design: 'Σχεδιασμός',
      domain: 'Domain',
      application: 'Εφαρμογή (SaaS)',
      support: 'Υποστήριξη',
      consulting: 'Σύμβουλος',
      training: 'Εκπαίδευση',
      license: 'Άδεια Λογισμικού',
      other: 'Άλλο',
    };
    const types = new Set(templateList.map((t) => t.service_type));
    if (currentType) {
      types.add(currentType);
    }
    return Array.from(types)
      .sort()
      .map((serviceType) => ({
        value: serviceType,
        label: serviceTypeLabels[serviceType] || serviceType,
      }));
  }, [templates]);

  // Handle client selection change
  const handleClientChange = (clientId) => {
    setSelectedClientId(clientId);
    setSelectedProjectId(null);
  };

  // Handle service type selection change (now includes templates)
  const handleServiceTypeChange = (serviceTypeValue, formData, setFormValue, handleChange) => {
    if (serviceTypeValue && templates.length > 0) {
      // Check if the selected value is a template (format: "template_123")
      if (serviceTypeValue.startsWith('template_')) {
        const templateId = parseInt(serviceTypeValue.replace('template_', ''));
        const template = templates.find(t => t.id === templateId);
        
        if (template && handleChange) {
          // Store the selected template
          setSelectedTemplate(template);
          
          // Calculate dates based on billing period
          const startDate = new Date().toISOString().split('T')[0];
          let endDate = '';
          let nextBillingDate = '';
          
          if (template.billing_period !== 'one_time') {
            const startDateObj = new Date(startDate);
            let endDateObj = new Date(startDateObj);
            let nextBillingDateObj = new Date(startDateObj);
            
            switch (template.billing_period) {
              case 'monthly':
                endDateObj.setMonth(endDateObj.getMonth() + 1);
                nextBillingDateObj.setMonth(nextBillingDateObj.getMonth() + 1);
                break;
              case 'quarterly':
                endDateObj.setMonth(endDateObj.getMonth() + 3);
                nextBillingDateObj.setMonth(nextBillingDateObj.getMonth() + 3);
                break;
              case 'semi_annual':
                endDateObj.setMonth(endDateObj.getMonth() + 6);
                nextBillingDateObj.setMonth(nextBillingDateObj.getMonth() + 6);
                break;
              case 'annual':
                endDateObj.setFullYear(endDateObj.getFullYear() + 1);
                nextBillingDateObj.setFullYear(nextBillingDateObj.getFullYear() + 1);
                break;
              case 'biennial':
                endDateObj.setFullYear(endDateObj.getFullYear() + 2);
                nextBillingDateObj.setFullYear(nextBillingDateObj.getFullYear() + 2);
                break;
            }
            
            endDate = endDateObj.toISOString().split('T')[0];
            nextBillingDate = nextBillingDateObj.toISOString().split('T')[0];
          }
          
          // Auto-fill form fields with template data using setFormValue
          setFormValue('name', template.name);
          
          setFormValue('service_type', template.service_type);
          
          setFormValue('price', parseFloat(template.default_price) || 0);
          
          setFormValue('billing_period', template.billing_period);
          
          setFormValue('vat_rate', parseFloat(template.vat_rate) || 0);
          
          setFormValue('start_date', startDate);
          
          setFormValue('end_date', endDate);
          
          setFormValue('next_billing_date', nextBillingDate);
          
          // Prepare template form data for state tracking
          const templateData = {
            name: template.name,
            service_type: template.service_type,
            price: parseFloat(template.default_price) || 0,
            billing_period: template.billing_period,
            vat_rate: parseFloat(template.vat_rate) || 0,
            start_date: startDate,
            end_date: endDate,
            next_billing_date: nextBillingDate
          };
          
          setTemplateFormData(templateData);
        }
      } else {
        // Handle regular service type selection (not template)
        if (serviceTypeValue && handleChange) {
          // Auto-generate name based on service type if name is empty or generic
          const currentName = formData.name || '';
          const serviceTypeLabels = {
            'hosting': 'Hosting Πακέτο',
            'development': 'Ανάπτυξη',
            'maintenance': 'Συντήρηση',
            'design': 'Σχεδιασμός',
            'domain': 'Domain',
            'other': 'Άλλη Υπηρεσία'
          };
          
          // Only update name if it's empty or seems generic
          if (!currentName || currentName === 'Νέα Υπηρεσία' || currentName === '') {
            const suggestedName = serviceTypeLabels[serviceTypeValue] || 'Νέα Υπηρεσία';
            setFormValue('name', suggestedName);
          }
          
          // Set default billing period based on service type
          let defaultBillingPeriod = 'one_time';
          if (serviceTypeValue === 'hosting' || serviceTypeValue === 'maintenance') {
            defaultBillingPeriod = 'annual';
          }
          
          setFormValue('billing_period', defaultBillingPeriod);
          
          // Calculate dates if billing period is not one_time
          if (defaultBillingPeriod !== 'one_time') {
            const startDate = formData.start_date || new Date().toISOString().split('T')[0];
            let endDate = '';
            let nextBillingDate = '';
            
            const startDateObj = new Date(startDate);
            let endDateObj = new Date(startDateObj);
            let nextBillingDateObj = new Date(startDateObj);
            
            switch (defaultBillingPeriod) {
              case 'monthly':
                endDateObj.setMonth(endDateObj.getMonth() + 1);
                nextBillingDateObj.setMonth(nextBillingDateObj.getMonth() + 1);
                break;
              case 'quarterly':
                endDateObj.setMonth(endDateObj.getMonth() + 3);
                nextBillingDateObj.setMonth(nextBillingDateObj.getMonth() + 3);
                break;
              case 'semi_annual':
                endDateObj.setMonth(endDateObj.getMonth() + 6);
                nextBillingDateObj.setMonth(nextBillingDateObj.getMonth() + 6);
                break;
              case 'annual':
                endDateObj.setFullYear(endDateObj.getFullYear() + 1);
                nextBillingDateObj.setFullYear(nextBillingDateObj.getFullYear() + 1);
                break;
              case 'biennial':
                endDateObj.setFullYear(endDateObj.getFullYear() + 2);
                nextBillingDateObj.setFullYear(nextBillingDateObj.getFullYear() + 2);
                break;
            }
            
            endDate = endDateObj.toISOString().split('T')[0];
            nextBillingDate = nextBillingDateObj.toISOString().split('T')[0];
            
            setFormValue('end_date', endDate);
            
            setFormValue('next_billing_date', nextBillingDate);
          }
        }
        
        // Clear template data if not a template selection
        setSelectedTemplate(null);
        setTemplateFormData(null);
      }
    }
  };

  // Handle actions
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleCreate = () => {
    setSelectedService(null);
    setSelectedClientId(null);
    setSelectedProjectId(null);
    setSelectedTemplate(null);
    setTemplateFormData(null);
    setFormResetKey(prev => prev + 1); // Force form reset
    setCreateModalOpen(true);
  };

  const handleCreateInvoice = () => {
    setPreSelectedData(null); // Clear pre-selected data
    setServiceSelectionModal(true);
  };

  // Multi-selection handlers
  const handleToggleMultiSelect = () => {
    setMultiSelectMode(!multiSelectMode);
    if (multiSelectMode) {
      setSelectedServices([]);
    }
  };

  const handleServiceSelect = (serviceId) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedServices.length === services.length) {
      setSelectedServices([]);
    } else {
      setSelectedServices(services.map(service => service.id));
    }
  };

  const maxWarningDays = useMemo(() => {
    const days = notificationSettings.email_warning_days || [10, 7, 3];
    return Math.max(...days, 0);
  }, [notificationSettings.email_warning_days]);

  const isServiceEligibleForExpirationEmail = useCallback((service) => {
    if (!notificationSettings.email_enabled) return false;
    if (!service?.end_date || service.billing_period === 'one_time') return false;
    if (service.status !== 'active') return false;
    const today = new Date();
    const end = new Date(service.end_date);
    const daysLeft = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return daysLeft > 0 && daysLeft <= maxWarningDays;
  }, [notificationSettings.email_enabled, maxWarningDays]);

  const eligibleSelectedServiceIds = useMemo(() => {
    return services
      .filter((service) => selectedServices.includes(service.id) && isServiceEligibleForExpirationEmail(service))
      .map((service) => service.id);
  }, [services, selectedServices, isServiceEligibleForExpirationEmail]);

  const isExpirationEmailBusy = sendingExpirationEmailId !== null || sendingBulkExpirationEmail;

  const handleSendExpirationWarnings = async (serviceIds) => {
    if (!serviceIds?.length || expirationEmailLockRef.current) {
      return;
    }

    expirationEmailLockRef.current = true;
    const isSingle = serviceIds.length === 1;
    if (isSingle) {
      setSendingExpirationEmailId(serviceIds[0]);
    } else {
      setSendingBulkExpirationEmail(true);
    }
    setError(null);
    setSuccess(null);

    try {
      const result = await ServiceService.sendExpirationWarnings(serviceIds);
      if (!result.success) {
        setError(result.error || 'Αποτυχία αποστολής email');
        return;
      }

      const { emails_sent: emailsSent = 0, service_ids: notifiedIds = [], errors = [] } = result.data || {};

      if (emailsSent > 0) {
        const serviceCount = notifiedIds.length || serviceIds.length;
        let message;
        if (emailsSent === 1 && serviceCount === 1) {
          message = 'Το email υπενθύμισης λήξης στάλθηκε επιτυχώς';
        } else {
          message = `Αποστάληκαν ${emailsSent} email για ${serviceCount} υπηρεσίες`;
        }
        if (errors.length > 0) {
          message += `. Προσοχή: ${errors.join(' · ')}`;
        }
        setSuccess(message);
      } else if (errors.length > 0) {
        setError(errors.join(' · '));
      } else {
        setError('Δεν αποστάληκε κανένα email');
      }
    } catch (err) {
      setError('Σφάλμα αποστολής email λήξης');
    } finally {
      expirationEmailLockRef.current = false;
      setSendingExpirationEmailId(null);
      setSendingBulkExpirationEmail(false);
    }
  };

  const handleSendExpirationEmailForService = (service) => {
    handleSendExpirationWarnings([service.id]);
  };

  const handleSendExpirationEmailForSelected = () => {
    handleSendExpirationWarnings(eligibleSelectedServiceIds);
  };

  const handleCreateInvoiceFromSelected = () => {
    const selectedServicesData = services.filter(service => selectedServices.includes(service.id));
    if (selectedServicesData.length > 0) {
      // Get unique client from selected services
      const clientId = selectedServicesData[0].client_id;
      const allSameClient = selectedServicesData.every(service => service.client_id === clientId);
      
      if (allSameClient) {
        // Prepare pre-selected data
        const preSelected = {
          client_id: clientId,
          services: selectedServicesData.map(service => ({
            id: service.id,
            name: service.name,
            description: service.description,
            price: service.price,
            quantity: 1,
            vat_rate: 0 // Default VAT rate
          }))
        };
        
        setPreSelectedData(preSelected);
        setServiceSelectionModal(true);
      } else {
        setError('Οι επιλεγμένες υπηρεσίες πρέπει να είναι από τον ίδιο πελάτη');
      }
    }
  };

  const handleEdit = (service) => {
    setSelectedService(service);
    setSelectedClientId(service.client_id);
    setEditModalOpen(true);
  };

  const handleDelete = (service) => {
    setSelectedService(service);
    setDeleteDialogOpen(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Form submission handlers
  const handleCreateSubmit = async (formData) => {
    try {
      const result = await ServiceService.create(formData);
      if (result.success) {
        setCreateModalOpen(false);
        loadServices();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Σφάλμα δημιουργίας υπηρεσίας' };
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      const result = await ServiceService.update(selectedService.id, formData);
      if (result.success) {
        setEditModalOpen(false);
        loadServices();
        loadStatistics(); // Reload statistics after update (e.g., when status changes to cancelled)
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Σφάλμα ενημέρωσης υπηρεσίας' };
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await ServiceService.delete(selectedService.id);
      if (result.success) {
        setDeleteDialogOpen(false);
        loadServices();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Σφάλμα διαγραφής υπηρεσίας');
    }
  };


  // Custom search header with additional filters
  const renderSearchHeader = () => (
    <Box sx={{ mb: 3 }}>
      <SearchHeader
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Αναζήτηση υπηρεσιών (όνομα, περιγραφή, πελάτης)..."
        statusFilter={clientStatusFilter}
        onStatusFilterChange={setClientStatusFilter}
        statusLabel="Πελάτες"
        statusOptions={[
          { value: 'all', label: 'Όλοι' },
          { value: 'active', label: 'Ενεργοί' },
          { value: 'inactive', label: 'Ανενεργοί' }
        ]}
        extraFilters={
          <Box sx={{ minWidth: { xs: 180, sm: 220 }, flex: { xs: '1 1 180px', sm: '0 0 auto' } }}>
            <ClientSelect
              value={clientFilter !== 'all' ? clientFilter : ''}
              onChange={(selectedId) => {
                setClientFilter(selectedId || 'all');
              }}
              label="Πελάτης"
              placeholder="Αναζήτηση πελάτη..."
              size="small"
            />
          </Box>
        }
        sortField={sortField}
        sortDirection={sortDirection}
        onSortChange={handleSort}
        sortOptions={[
          { value: 'name', label: 'Όνομα' },
          { value: 'client_name', label: 'Πελάτης' },
          { value: 'project_name', label: 'Έργο' },
          { value: 'price', label: 'Τιμή' },
          { value: 'target_amount', label: 'Συνολικό Ποσό' },
          { value: 'balance', label: 'Υπόλοιπο' },
          { value: 'paid_amount', label: 'Εξοφλημένο' },
          { value: 'start_date', label: 'Ημ. Έναρξης' },
          { value: 'end_date', label: 'Ημ. Λήξης' },
          { value: 'created_at', label: 'Ημ. Δημιουργίας' },
        ]}
      />
      
      {/* Additional Filters */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Κατάσταση Πληρωμής</InputLabel>
          <Select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            label="Κατάσταση Πληρωμής"
          >
            <MenuItem value="all">Όλες</MenuItem>
            <MenuItem value="unpaid">Ανεξόφλητες</MenuItem>
            <MenuItem value="partial">Μερικώς εξοφλημένες</MenuItem>
            <MenuItem value="paid">Εξοφλημένες</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Τύπος Υπηρεσίας</InputLabel>
          <Select
            value={serviceTypeFilter}
            onChange={(e) => setServiceTypeFilter(e.target.value)}
            label="Τύπος Υπηρεσίας"
          >
            <MenuItem value="all">Όλοι</MenuItem>
            <MenuItem value="development">Ανάπτυξη</MenuItem>
            <MenuItem value="hosting">Hosting</MenuItem>
            <MenuItem value="maintenance">Συντήρηση</MenuItem>
            <MenuItem value="seo">SEO</MenuItem>
            <MenuItem value="design">Σχεδιασμός</MenuItem>
            <MenuItem value="other">Άλλο</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Χρέωση</InputLabel>
          <Select
            value={chargeFilter}
            onChange={(e) => setChargeFilter(e.target.value)}
            label="Χρέωση"
          >
            <MenuItem value="all">Όλα</MenuItem>
            <MenuItem value="one_time">Εφάπαξ</MenuItem>
            <MenuItem value="subscription">Συνδρομητικές</MenuItem>
            <ListSubheader>Περίοδος συνδρομής</ListSubheader>
            <MenuItem value="monthly">Μηνιαία</MenuItem>
            <MenuItem value="quarterly">Τριμηνιαία</MenuItem>
            <MenuItem value="semi_annual">Εξαμηνιαία</MenuItem>
            <MenuItem value="annual">Ετήσια</MenuItem>
            <MenuItem value="biennial">Διετής</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }} variant="outlined">
          <InputLabel id="service-year-filter-label" shrink>Έτος</InputLabel>
          <Select
            labelId="service-year-filter-label"
            multiple
            displayEmpty
            notched
            value={yearFilter}
            onChange={(e) => {
              const value = e.target.value;
              const selected = typeof value === 'string' ? value.split(',') : value;
              setYearFilter(selected.map(Number).filter((year) => !Number.isNaN(year)));
            }}
            label="Έτος"
            renderValue={(selected) => (selected.length ? selected.join(', ') : 'Όλα')}
          >
            {yearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                <Checkbox checked={yearFilter.includes(year)} size="small" />
                <ListItemText primary={year} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Ισχύς</InputLabel>
          <Select
            value={validityFilter}
            onChange={(e) => setValidityFilter(e.target.value)}
            label="Ισχύς"
          >
            <MenuItem value="all">Όλες</MenuItem>
            <MenuItem value="in_force">Σε ισχύ τώρα</MenuItem>
            <ListSubheader>Λήξη</ListSubheader>
            <MenuItem value="expiring_today">Λήγουν σήμερα</MenuItem>
            <MenuItem value="expiring_7_days">Λήγουν σε 7 ημέρες</MenuItem>
            <MenuItem value="expiring_30_days">Λήγουν σε 30 ημέρες</MenuItem>
            <MenuItem value="expiring_60_days">Λήγουν σε 60 ημέρες</MenuItem>
            <MenuItem value="expiring_90_days">Λήγουν σε 90 ημέρες</MenuItem>
            <MenuItem value="expired">Έχουν λήξει</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Κατάσταση</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Κατάσταση"
          >
            <MenuItem value="all">Όλες</MenuItem>
            <MenuItem value="on_hold">Σε Αναμονή</MenuItem>
            <MenuItem value="grace_period">Περίοδος Χάριτος</MenuItem>
            <MenuItem value="suspended">Αναστολή</MenuItem>
            <MenuItem value="completed">Ολοκληρωμένη</MenuItem>
            <MenuItem value="cancelled">Ακυρωμένη</MenuItem>
            <MenuItem value="pending">Εκκρεμής</MenuItem>
          </Select>
        </FormControl>
        
        {/* Multi-select toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={multiSelectMode}
              onChange={handleToggleMultiSelect}
              color="primary"
            />
          }
          label="Πολλαπλή Επιλογή"
          sx={{ ml: 2 }}
        />
        
        {/* Create invoice from selected services button (only visible when services are selected) */}
        {multiSelectMode && selectedServices.length > 0 && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ReceiptIcon />}
            onClick={handleCreateInvoiceFromSelected}
            sx={{ ml: 2 }}
          >
            Δημιουργία Τιμολογίου ({selectedServices.length})
          </Button>
        )}

        {multiSelectMode && eligibleSelectedServiceIds.length > 0 && notificationSettings.email_enabled && (
          <Button
            variant="contained"
            color="info"
            startIcon={isExpirationEmailBusy ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
            onClick={handleSendExpirationEmailForSelected}
            disabled={isExpirationEmailBusy}
            sx={{ ml: 2 }}
          >
            {isExpirationEmailBusy && sendingBulkExpirationEmail
              ? 'Αποστολή...'
              : `Αποστολή Email Λήξης (${eligibleSelectedServiceIds.length})`}
          </Button>
        )}
      </Box>
    </Box>
  );

  // Page header actions
  const pageActions = [
    
    // Create service button
    <IconButton
      key="create"
      color="primary"
      onClick={handleCreate}
      sx={{ 
        backgroundColor: 'primary.main',
        color: 'white',
        '&:hover': {
          backgroundColor: 'primary.dark',
        }
      }}
    >
      <AddIcon />
    </IconButton>
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <PageHeader
        title="Διαχείριση Υπηρεσιών"
        subtitle="Προβολή και διαχείριση όλων των υπηρεσιών"
        icon="⚙️"
        actions={pageActions}
      />

      {/* Statistics Cards */}
      <Grid container spacing={isXs ? 1 : 2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Σύνολο Υπηρεσιών"
            value={statistics.total_services}
            color="primary"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Συνολικό Κόστος"
            value={formatCurrency(statistics.total_cost_with_vat || statistics.total_credit || 0)}
            subtitle={statistics.net_cost !== undefined ? `Καθαρό κόστος: ${formatCurrency(statistics.net_cost || 0)}` : null}
            color="info"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Εξοφλημένο"
            value={formatCurrency(statistics.total_paid)}
            color="success"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Υπόλοιπο"
            value={formatCurrency(statistics.balance)}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Υπηρεσίες" value="services" />
          <Tab label="Προγραμματιστής" value="scheduler" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 'services' && (
        <>
          {/* Search and Filters */}
          {renderSearchHeader()}
        </>
      )}

      {/* Tab Content */}
      {activeTab === 'services' && (
        <>
          {/* Data Table */}
            <ServiceTable
              services={services}
              loading={loading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              multiSelectMode={multiSelectMode}
              selectedServices={selectedServices}
              onServiceSelect={handleServiceSelect}
              onSelectAll={handleSelectAll}
              emailWarningDays={notificationSettings.email_warning_days}
              emailEnabled={notificationSettings.email_enabled}
              onSendExpirationEmail={handleSendExpirationEmailForService}
              sendingExpirationEmailId={sendingExpirationEmailId}
              isExpirationEmailBusy={isExpirationEmailBusy}
            />

          {/* Pagination */}
          <PaginationComponent
            currentPage={currentPage}
            totalPages={Math.ceil(totalServices / itemsPerPage)}
            totalItems={totalServices}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </>
      )}

      {activeTab === 'scheduler' && (
        <SchedulerPanel />
      )}

      {/* Create Modal */}
      <FormBuilder
        key={`create-${formResetKey}`}
        open={createModalOpen}
        entity={null}
        onClose={() => {
          setCreateModalOpen(false);
          setSelectedTemplate(null);
          setTemplateFormData(null);
          setSelectedClientId(null);
        }}
        schema={{
          ...createServiceSchema,
          initialValues: {
            ...createServiceSchema.initialValues,
            ...(templateFormData || {})
          }
        }}
        onSubmit={handleCreateSubmit}
        options={{
          project_id: getFilteredProjects().map(project => ({ value: project.id, label: project.name })),
          service_type: getServiceTypeOptions()
        }}
        onFieldChange={(fieldName, value, formData, setFormValue, handleChange) => {
          if (fieldName === 'client_id') {
            handleClientChange(value);
            // Clear project_id when client changes
            setFormValue('project_id', '');
          } else if (fieldName === 'service_type') {
            handleServiceTypeChange(value, formData, setFormValue, handleChange);
          } else if (fieldName === 'name' && value && !formData.service_type) {
            // Auto-set service_type to 'other' when user types a custom name
            handleChange('service_type', 'other');
          }
        }}
        modalProps={{
          title: 'Δημιουργία Νέας Υπηρεσίας',
          maxWidth: 'lg'
        }}
      />

      {/* Edit Modal */}
      <FormBuilder
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedTemplate(null);
          setTemplateFormData(null);
        }}
        schema={{
          ...updateServiceSchema,
          // Don't merge templateFormData in edit mode - it causes reset when selecting template
          initialValues: updateServiceSchema.initialValues     // ✅ ΜΟΝΟ ΑΥΤΟ
        }}
        entity={selectedService}
        onSubmit={handleEditSubmit}
        options={{
          project_id: getFilteredProjects().map(project => ({ value: project.id, label: project.name })),
          service_type: getEditServiceTypeOptions(selectedService?.service_type)
        }}
        onFieldChange={(fieldName, value, formData, setFormValue) => {
          if (fieldName === 'client_id') {
            handleClientChange(value);
            setFormValue('project_id', '');
          }
        }}
        modalProps={{
          title: 'Επεξεργασία Υπηρεσίας',
          maxWidth: 'lg'
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Διαγραφή Υπηρεσίας"
        message={`Είστε σίγουροι ότι θέλετε να διαγράψετε την υπηρεσία "${selectedService?.name}"?`}
        confirmText="Διαγραφή"
        cancelText="Ακύρωση"
      />

      {/* Reusable Invoice Modal for Invoice Creation from Services */}
      <InvoiceFormModal
        open={serviceSelectionModal}
        onClose={() => {
          setServiceSelectionModal(false);
          setPreSelectedData(null);
        }}
        onSuccess={() => {
          setServiceSelectionModal(false);
          setPreSelectedData(null);
          setSuccess('Το τιμολόγιο δημιουργήθηκε επιτυχώς');
        }}
        mode="createFromServices"
        invoice={null}
        preSelectedData={preSelectedData}
      />

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={8000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={5000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default ServiceListingPage;
